import React, { useState } from 'react';
import {
  OpportunityItem, TechnicalStatus, OpportunityStatus,
  STATUS_COLOR, NEXT_STATUS,
} from '../models/opportunity';
import { OpportunityDetailModal } from './OpportunityDetailModal';

/* ─── helpers ───────────────────────────────────────────────────── */
const isSlaCritical = (d?: string) =>
  !!d && (new Date(d).getTime() - Date.now()) / 3600000 <= 48;

// Phases where técnico is the actor
const TECNICO_PHASES: OpportunityStatus[] = [
  '2. Levantamento de Necessidades',
  '3. Especificação Técnica',
  '8. Em Execução',
  '9. Entrega & Validação',
];

// After técnico marks "Concluído" in these phases, StatusOportunidade advances
const TECNICO_ADVANCES: Partial<Record<OpportunityStatus, OpportunityStatus>> = {
  '2. Levantamento de Necessidades': '3. Especificação Técnica',
  '3. Especificação Técnica':        '4. Proposta em Elaboração',
  '9. Entrega & Validação':          '10. Concluído / Ganho',
};

// Action text per phase per role
const GUIDE: Record<string, Partial<Record<OpportunityStatus, string>>> = {
  comercial: {
    '1. Levantamento Inicial':        'Confirmar atribuição técnica e enviar para levantamento',
    '4. Proposta em Elaboração':      'Calcular margens, obter aprovação financeira',
    '5. Proposta Enviada':            'Aguardar decisão do cliente',
    '6. Decisão do Cliente':          'Registar decisão: Ganho (avançar) ou Perdido',
    '7. Ordem de Trabalho Gerada':    'Emitir número de OT e confirmar arranque',
    '10. Concluído / Ganho':          '✅ Projeto concluído com sucesso',
    '11. Perdido / Cancelado':        '❌ Oportunidade arquivada',
  },
  tecnico: {
    '2. Levantamento de Necessidades': 'Fazer visita ao cliente e registar necessidades identificadas',
    '3. Especificação Técnica':        'Dimensionar solução, listar materiais e estimar horas de trabalho',
    '8. Em Execução':                  'Executar obra/serviço e actualizar % de progresso',
    '9. Entrega & Validação':          'Validar entrega com o cliente e confirmar conclusão',
  },
};

const ActionBox: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div style={{ background: `${color}12`, border: `1px solid ${color}33`, borderRadius: 8, padding: '8px 11px', marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
    <span style={{ fontSize: 14, marginTop: 1 }}>👉</span>
    <span style={{ fontSize: 12, color, lineHeight: 1.4, fontWeight: 500 }}>{text}</span>
  </div>
);

/* ─── Execution update inline modal ─────────────────────────────── */
const ExecModal: React.FC<{
  item: OpportunityItem;
  currentUserName: string;
  phase: '8. Em Execução' | '9. Entrega & Validação';
  onSave: (id: string, progresso: number, entrada: string, diarioActual: string) => Promise<void>;
  onAdvance: (id: string, next: OpportunityStatus) => Promise<void>;
  onClose: () => void;
}> = ({ item, currentUserName, phase, onSave, onAdvance, onClose }) => {
  const [progresso, setProgresso] = useState(item.progressoExecucao ?? 0);
  const [entrada, setEntrada] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const entradaFinal = entrada.trim() || `Progresso actualizado: ${progresso}%`;
      await onSave(item.id, progresso, entradaFinal, item.diarioBordo || '');
      onClose();
    } finally { setSaving(false); }
  };

  const handleConclude = async () => {
    setSaving(true);
    try {
      // Always advance; optionally add a diary entry first
      const finalProgresso = phase === '9. Entrega & Validação' ? 100 : progresso;
      const entradaFinal = entrada.trim()
        ? entrada
        : phase === '9. Entrega & Validação'
          ? 'Entrega e validação concluída.'
          : 'Execução concluída.';
      await onSave(item.id, finalProgresso, entradaFinal, item.diarioBordo || '');
      await onAdvance(item.id, TECNICO_ADVANCES[phase]!);
      onClose();
    } finally { setSaving(false); }
  };

  const isEntrega = phase === '9. Entrega & Validação';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 16, width: '100%', maxWidth: 500, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', background: 'var(--chk-navy)', borderBottom: '1px solid var(--chk-border)', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--chk-white)' }}>{isEntrega ? '✅ Entrega & Validação' : '⚙️ Actualizar Execução'}</div>
            <div style={{ fontSize: 12, color: 'var(--chk-muted)', marginTop: 2 }}>{item.codigoOportunidade} · {item.cliente}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chk-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Progresso slider */}
          {!isEntrega && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 11, color: 'var(--chk-muted)', textTransform: 'uppercase', letterSpacing: .5 }}>Progresso de Execução</label>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#60A5FA' }}>{progresso}%</span>
              </div>
              <div style={{ height: 8, background: '#1A1F35', borderRadius: 4, marginBottom: 8, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progresso}%`, background: progresso >= 100 ? '#22C55E' : '#1755B8', borderRadius: 4, transition: 'width .2s' }} />
              </div>
              <input type="range" min={0} max={100} value={progresso} onChange={e => setProgresso(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#1755B8', cursor: 'pointer' }} />
            </div>
          )}
          {/* Diary entry */}
          <div>
            <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: .5 }}>
              {isEntrega ? 'Nota de Validação / Entrega' : 'Entrada no Diário de Bordo'}
            </label>
            <textarea value={entrada} onChange={e => setEntrada(e.target.value)} rows={4}
              placeholder={isEntrega
                ? 'Descreve a validação feita com o cliente, TAD assinado, etc...'
                : 'Trabalho realizado hoje, marcos atingidos, obstáculos...'}
              style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '9px 12px', color: 'var(--chk-text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {/* Previous entries preview */}
          {item.diarioBordo && (
            <div style={{ background: 'rgba(255,255,255,.02)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '9px 12px', fontSize: 11, color: 'var(--chk-muted)', maxHeight: 80, overflow: 'hidden' }}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10, textTransform: 'uppercase' }}>Última entrada</div>
              {item.diarioBordo.split('\n\n---\n\n')[0]?.substring(0, 150)}...
            </div>
          )}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--chk-border)', display: 'flex', gap: 8, background: 'rgba(0,0,0,.2)' }}>
          <button onClick={onClose} style={{ flex: 1, background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', color: 'var(--chk-muted)', borderRadius: 8, padding: '9px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          {!isEntrega && (
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 1, background: saving ? '#333' : 'linear-gradient(135deg,#1755B8,#0F3F8C)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳...' : '💾 Gravar'}
            </button>
          )}
          <button onClick={handleConclude} disabled={saving}
            style={{ flex: 2, background: saving ? '#333' : 'linear-gradient(135deg,#22C55E,#16A34A)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '⏳...' : isEntrega ? '✅ Confirmar Entrega & Concluir' : '🏁 Concluir Execução → Entrega'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── main component ─────────────────────────────────────────────── */
interface CardProps {
  item: OpportunityItem;
  currentUserEmail: string;
  currentUserName: string;
  onUpdate: (id: string, status: TechnicalStatus, parecer: string, historicoActual: string, novoStatusOpp?: OpportunityStatus) => Promise<void>;
  onAdvanceStatus: (id: string, newStatus: OpportunityStatus, extra?: Record<string, any>) => Promise<void>;
  onUpdateExecution: (id: string, progresso: number, entrada: string, diarioActual: string) => Promise<void>;
}

export const OpportunityCard: React.FC<CardProps> = ({
  item, currentUserEmail, currentUserName,
  onUpdate, onAdvanceStatus, onUpdateExecution,
}) => {
  const [showParecer, setShowParecer] = useState(false);
  const [showExec, setShowExec] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [novoStatusTec, setNovoStatusTec] = useState<TechnicalStatus>(item.statusTecnico);
  const [parecerTexto, setParecerTexto] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const email = currentUserEmail.toLowerCase();
  const isMyComercial = item.comercialEmail?.toLowerCase() === email;
  const isMyTecnico   = item.responsavelTecnicoEmail?.toLowerCase() === email;

  const isTecnicoPhase = TECNICO_PHASES.includes(item.statusOportunidade);
  const isComercialPhase = !isTecnicoPhase;
  const isTerminal = ['10. Concluído / Ganho', '11. Perdido / Cancelado'].includes(item.statusOportunidade);

  const myTurn = (!isTerminal) && (
    (isMyTecnico && isTecnicoPhase) ||
    (isMyComercial && isComercialPhase)
  );

  const urgent = isSlaCritical(item.dataLimiteParecer);
  const oppColor = STATUS_COLOR[item.statusOportunidade];
  const nextStatus = NEXT_STATUS[item.statusOportunidade];
  const phaseNum = parseInt(item.statusOportunidade.split('.')[0], 10);
  const phaseName = item.statusOportunidade.replace(/^\d+\. /, '');

  const guideText = isMyTecnico
    ? GUIDE.tecnico[item.statusOportunidade]
    : isMyComercial
      ? GUIDE.comercial[item.statusOportunidade]
      : undefined;

  // Técnico: after marking "Parecer Concluído" in N2/N3, status advances automatically
  const handleSubmitParecer = async () => {
    if (!parecerTexto.trim()) return;
    setSubmitting(true);
    try {
      const isConcluido = novoStatusTec === 'Parecer Técnico Concluído';
      const novoStatusOpp = isConcluido ? TECNICO_ADVANCES[item.statusOportunidade] : undefined;
      await onUpdate(item.id, novoStatusTec, parecerTexto, item.historicoParecer || '', novoStatusOpp);
      setShowParecer(false);
      setParecerTexto('');
    } finally { setSubmitting(false); }
  };

  /* ─── render ── */
  return (
    <>
      <div style={{
        background: 'var(--chk-card)', borderRadius: 14, overflow: 'hidden',
        border: `1px solid ${myTurn ? oppColor + '55' : 'var(--chk-border)'}`,
        borderLeft: `4px solid ${isTerminal
          ? (item.statusOportunidade.includes('Ganho') ? '#22C55E' : '#EF4444')
          : myTurn ? oppColor : '#2A3050'}`,
        opacity: isTerminal && !isMyComercial ? 0.65 : 1,
        transition: 'transform .15s, box-shadow .15s',
      }}
        onMouseEnter={e => { const d = e.currentTarget as HTMLDivElement; d.style.transform = 'translateY(-2px)'; d.style.boxShadow = '0 8px 32px rgba(0,0,0,.4)'; }}
        onMouseLeave={e => { const d = e.currentTarget as HTMLDivElement; d.style.transform = ''; d.style.boxShadow = ''; }}
      >
        {/* SLA warning bar */}
        {urgent && !isTerminal && <div style={{ height: 3, background: '#EF4444' }} />}

        {/* Phase ribbon */}
        <div style={{ background: `${oppColor}14`, borderBottom: `1px solid ${oppColor}28`, padding: '7px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: oppColor, color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>N{phaseNum}</div>
            <span style={{ fontSize: 12, fontWeight: 600, color: oppColor }}>{phaseName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {urgent && <span style={{ fontSize: 10, fontWeight: 700, color: '#EF4444', background: 'rgba(239,68,68,.12)', padding: '2px 7px', borderRadius: 20 }}>⚠️ SLA</span>}
            {item.numeroOT && <span style={{ fontSize: 10, fontWeight: 600, color: '#34D399', background: 'rgba(52,211,153,.1)', padding: '2px 7px', borderRadius: 20 }}>{item.numeroOT}</span>}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '11px 14px' }}>
          {/* Code + client */}
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--chk-white)' }}>{item.codigoOportunidade}</div>
          <div style={{ fontSize: 12, color: 'var(--chk-muted)', marginTop: 1, marginBottom: 10 }}>🏢 {item.cliente}</div>

          {/* People */}
          <div style={{ display: 'flex', gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--chk-border)', marginBottom: 8 }}>
            <div style={{ flex: 1, background: 'rgba(204,41,54,.06)', borderRadius: 7, padding: '6px 9px' }}>
              <div style={{ fontSize: 9, color: '#CC2936', textTransform: 'uppercase', letterSpacing: .5, fontWeight: 700 }}>Comercial</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: isMyComercial ? '#CC2936' : 'var(--chk-text)', marginTop: 2 }}>{item.comercialNome}</div>
            </div>
            <div style={{ flex: 1, background: 'rgba(23,85,184,.06)', borderRadius: 7, padding: '6px 9px' }}>
              <div style={{ fontSize: 9, color: '#1755B8', textTransform: 'uppercase', letterSpacing: .5, fontWeight: 700 }}>Técnico DTI</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: isMyTecnico ? '#60A5FA' : 'var(--chk-text)', marginTop: 2 }}>{item.responsavelTecnicoNome || '—'}</div>
            </div>
          </div>

          {/* Âmbito */}
          <div style={{ fontSize: 12, color: 'var(--chk-text)', lineHeight: 1.4, marginBottom: 6,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {item.ambitoNecessidades || '—'}
          </div>

          {/* Progresso bar */}
          {item.progressoExecucao != null && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 10, color: 'var(--chk-muted)' }}>Progresso execução</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#60A5FA' }}>{item.progressoExecucao}%</span>
              </div>
              <div style={{ height: 5, background: '#1A1F35', borderRadius: 3 }}>
                <div style={{ height: '100%', width: `${item.progressoExecucao}%`, background: item.progressoExecucao >= 100 ? '#22C55E' : '#1755B8', borderRadius: 3 }} />
              </div>
            </div>
          )}

          {/* Action guide — MY TURN */}
          {myTurn && guideText && (
            <ActionBox text={guideText} color={isMyTecnico ? '#1755B8' : '#CC2936'} />
          )}

          {/* Waiting state */}
          {!myTurn && !isTerminal && (
            <div style={{ fontSize: 11, color: '#3A3F55', background: 'rgba(255,255,255,.02)', borderRadius: 7, padding: '6px 10px', marginTop: 6 }}>
              ⏳ À espera de acção de: <b style={{ color: '#5A6080' }}>{isTecnicoPhase ? 'Técnico DTI' : 'Comercial'}</b>
            </div>
          )}
        </div>

        {/* ── Footer actions ── */}
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--chk-border)', background: 'rgba(0,0,0,.15)', display: 'flex', gap: 8 }}>

          {/* ── TÉCNICO: N2/N3 → parecer ── */}
          {isMyTecnico && (item.statusOportunidade === '2. Levantamento de Necessidades' || item.statusOportunidade === '3. Especificação Técnica') && (
            <button onClick={() => setShowParecer(true)} style={{
              flex: 2, background: 'linear-gradient(135deg,#1755B8,#0F3F8C)',
              color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>✏️ {item.statusOportunidade === '2. Levantamento de Necessidades' ? 'Registar Levantamento' : 'Submeter Especificação'}</button>
          )}

          {/* ── TÉCNICO: N8 → actualizar execução ── */}
          {isMyTecnico && item.statusOportunidade === '8. Em Execução' && (
            <button onClick={() => setShowExec(true)} style={{
              flex: 2, background: 'linear-gradient(135deg,#1755B8,#0F3F8C)',
              color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>⚙️ Actualizar Execução</button>
          )}

          {/* ── TÉCNICO: N9 → confirmar entrega ── */}
          {isMyTecnico && item.statusOportunidade === '9. Entrega & Validação' && (
            <button onClick={() => setShowExec(true)} style={{
              flex: 2, background: 'linear-gradient(135deg,#22C55E,#16A34A)',
              color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>✅ Confirmar Entrega</button>
          )}

          {/* ── COMERCIAL: advance button ── */}
          {isMyComercial && isComercialPhase && !isTerminal && nextStatus && (
            <button onClick={() => setShowDetail(true)} style={{
              flex: 2, background: `linear-gradient(135deg,${oppColor},${oppColor}BB)`,
              color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>➡️ {nextStatus.replace(/^\d+\. /, '')}</button>
          )}

          {/* ── Ver (detail) — always visible, secondary ── */}
          <button onClick={() => setShowDetail(true)} style={{
            flex: myTurn ? 0 : 1,
            background: 'var(--chk-navy)', border: '1px solid var(--chk-border)',
            color: 'var(--chk-text)', borderRadius: 8, padding: '10px 13px',
            fontSize: 12, cursor: 'pointer',
          }}>👁</button>
        </div>
      </div>

      {/* ══════════ Modal: Técnico Parecer (N2/N3) */}
      {showParecer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={e => e.target === e.currentTarget && setShowParecer(false)}>
          <div style={{ background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 16, width: '100%', maxWidth: 540, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: 'var(--chk-navy)', borderBottom: '1px solid var(--chk-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--chk-white)' }}>
                  {item.statusOportunidade === '2. Levantamento de Necessidades' ? '📋 Registo de Levantamento' : '🔧 Especificação Técnica'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--chk-muted)', marginTop: 2 }}>{item.codigoOportunidade} · {item.cliente}</div>
                <div style={{ fontSize: 11, color: '#1755B8', marginTop: 3 }}>N{phaseNum} — {phaseName}</div>
              </div>
              <button onClick={() => setShowParecer(false)} style={{ background: 'none', border: 'none', color: 'var(--chk-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: .5 }}>Status Técnico</label>
                <select value={novoStatusTec} onChange={e => setNovoStatusTec(e.target.value as TechnicalStatus)}
                  style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '9px 12px', color: 'var(--chk-text)', fontSize: 13 }}>
                  {item.statusOportunidade === '2. Levantamento de Necessidades' ? (
                    <>
                      <option value="Em Levantamento">Em Levantamento (ainda a decorrer)</option>
                      <option value="Aguardando Info Comercial/Cliente">Aguardando Info do Cliente</option>
                      <option value="Parecer Técnico Concluído">✅ Levantamento Concluído → avança para Especificação</option>
                    </>
                  ) : (
                    <>
                      <option value="Em Especificação Técnica">Em Especificação (ainda a decorrer)</option>
                      <option value="Aguardando Info Comercial/Cliente">Aguardando Info do Cliente</option>
                      <option value="Parecer Técnico Concluído">✅ Especificação Concluída → avança para Proposta</option>
                    </>
                  )}
                </select>
                {novoStatusTec === 'Parecer Técnico Concluído' && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 8, fontSize: 12, color: '#4ADE80' }}>
                    ℹ️ Ao submeter, a oportunidade avança automaticamente para a próxima fase com o Comercial.
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: .5 }}>
                  {item.statusOportunidade === '2. Levantamento de Necessidades' ? 'Registo do Levantamento' : 'Especificação Técnica'}
                </label>
                <textarea value={parecerTexto} onChange={e => setParecerTexto(e.target.value)} rows={6}
                  placeholder={item.statusOportunidade === '2. Levantamento de Necessidades'
                    ? 'Descreve as necessidades identificadas, infra existente, condicionantes...'
                    : 'Lista de materiais, estimativa de horas, dimensionamento da solução...'}
                  style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '9px 12px', color: 'var(--chk-text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--chk-border)', display: 'flex', gap: 8, background: 'rgba(0,0,0,.2)' }}>
              <button onClick={() => setShowParecer(false)} style={{ flex: 1, background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', color: 'var(--chk-muted)', borderRadius: 8, padding: '9px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleSubmitParecer} disabled={submitting || !parecerTexto.trim()}
                style={{ flex: 2, background: submitting || !parecerTexto.trim() ? '#333' : 'linear-gradient(135deg,#1755B8,#0F3F8C)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 600, cursor: submitting || !parecerTexto.trim() ? 'not-allowed' : 'pointer' }}>
                {submitting ? '⏳ A gravar...' : novoStatusTec === 'Parecer Técnico Concluído' ? '✅ Submeter e Avançar' : '💾 Gravar Progresso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Modal: Execução / Entrega (N8/N9) */}
      {showExec && (
        <ExecModal
          item={item}
          currentUserName={currentUserName}
          phase={item.statusOportunidade as '8. Em Execução' | '9. Entrega & Validação'}
          onSave={onUpdateExecution}
          onAdvance={(id, next) => onAdvanceStatus(id, next)}
          onClose={() => setShowExec(false)}
        />
      )}

      {/* ══════════ Modal: Detalhe (role-aware) */}
      {showDetail && (
        <OpportunityDetailModal
          item={item}
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          isMyComercial={isMyComercial}
          isTecnicoPhase={isTecnicoPhase}
          onClose={() => setShowDetail(false)}
          onAdvanceStatus={onAdvanceStatus}
          onUpdateExecution={onUpdateExecution}
        />
      )}
    </>
  );
};
